import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, FileText, MapPin, Newspaper, Search, Ship } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Input } from "@/components/ui/input";
import { publicSeoHubs } from "@/lib/publicSeoHubs";
import { getRegulationsHubTranslation } from "@/i18n";
import { marinaDetailPath } from "@/lib/marinaUrls";
import { languageLabels } from "@/lib/i18nRoutes";

function slugify(value = "") {
  return String(value).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const iconMap = { Regulation: FileText, Marina: Ship, Wire: Newspaper };

const searchCopy = {
  en: { placeholder: "Search regulations, marinas, Wire…", aria: "Search regulations, marinas, and Wire articles", minChars: "Type at least 2 characters to search regulations, marinas, and Wire articles.", noMatches: "No matches yet. Press Enter to open full search." },
  he: { placeholder: "חיפוש תקנות, מרינות וחדשות ימיות…", aria: "חיפוש תקנות, מרינות ומאמרי חדשות ימיות", minChars: "הקלידו לפחות 2 תווים כדי לחפש תקנות, מרינות וחדשות ימיות.", noMatches: "אין עדיין תוצאות. לחצו Enter לפתיחת חיפוש מלא." },
  es: { placeholder: "Buscar regulaciones, marinas, Wire…", aria: "Buscar regulaciones, marinas y artículos de Wire", minChars: "Escribe al menos 2 caracteres para buscar regulaciones, marinas y artículos de Wire.", noMatches: "Aún no hay coincidencias. Pulsa Enter para abrir la búsqueda completa." },
  it: { placeholder: "Cerca normative, marine, Wire…", aria: "Cerca normative, marine e articoli Wire", minChars: "Digita almeno 2 caratteri per cercare normative, marine e articoli Wire.", noMatches: "Nessun risultato ancora. Premi Invio per aprire la ricerca completa." },
  fr: { placeholder: "Rechercher réglementations, marinas, Wire…", aria: "Rechercher des réglementations, marinas et articles Wire", minChars: "Saisissez au moins 2 caractères pour rechercher des réglementations, marinas et articles Wire.", noMatches: "Aucun résultat pour le moment. Appuyez sur Entrée pour ouvrir la recherche complète." },
  de: { placeholder: "Vorschriften, Marinas, Wire suchen…", aria: "Vorschriften, Marinas und Wire-Artikel suchen", minChars: "Gib mindestens 2 Zeichen ein, um Vorschriften, Marinas und Wire-Artikel zu suchen.", noMatches: "Noch keine Treffer. Drücke Enter, um die vollständige Suche zu öffnen." },
  pt: { placeholder: "Pesquisar regulamentos, marinas, Wire…", aria: "Pesquisar regulamentos, marinas e artigos Wire", minChars: "Digite pelo menos 2 caracteres para pesquisar regulamentos, marinas e artigos Wire.", noMatches: "Ainda sem resultados. Pressione Enter para abrir a pesquisa completa." },
  ar: { placeholder: "ابحث في اللوائح والمراسي والأخبار البحرية…", aria: "البحث في اللوائح والمراسي ومقالات الأخبار البحرية", minChars: "اكتب حرفين على الأقل للبحث في اللوائح والمراسي والأخبار البحرية.", noMatches: "لا توجد نتائج بعد. اضغط Enter لفتح البحث الكامل." },
  el: { placeholder: "Αναζήτηση κανονισμών, μαρινών, Wire…", aria: "Αναζήτηση κανονισμών, μαρινών και άρθρων Wire", minChars: "Πληκτρολογήστε τουλάχιστον 2 χαρακτήρες για αναζήτηση κανονισμών, μαρινών και άρθρων Wire.", noMatches: "Δεν υπάρχουν αποτελέσματα ακόμα. Πατήστε Enter για πλήρη αναζήτηση." },
  ru: { placeholder: "Поиск правил, марин, Wire…", aria: "Поиск правил, марин и статей Wire", minChars: "Введите минимум 2 символа для поиска правил, марин и статей Wire.", noMatches: "Совпадений пока нет. Нажмите Enter, чтобы открыть полный поиск." },
  tr: { placeholder: "Kurallar, marinalar, Wire ara…", aria: "Kurallar, marinalar ve Wire yazılarında ara", minChars: "Kurallar, marinalar ve Wire yazılarında aramak için en az 2 karakter yazın.", noMatches: "Henüz eşleşme yok. Tam aramayı açmak için Enter’a basın." },
  zh: { placeholder: "搜索法规、码头、Wire…", aria: "搜索法规、码头和 Wire 文章", minChars: "至少输入 2 个字符以搜索法规、码头和 Wire 文章。", noMatches: "暂无匹配结果。按 Enter 打开完整搜索。" },
};

const resultLabels = {
  en: { Regulation: "Regulation", Marina: "Marina", Wire: "Wire", marina: "Marina and berth information", wire: "Marstick Wire briefing" },
  he: { Regulation: "תקנה", Marina: "מרינה", Wire: "חדשות ימיות", marina: "מידע מרינה ועגינה", wire: "תדריך חדשות ימי של Marstick" },
  es: { Regulation: "Regulación", Marina: "Marina", Wire: "Noticias marítimas", marina: "Información de marina y atraque", wire: "Briefing marítimo de Marstick Wire" },
  it: { Regulation: "Normativa", Marina: "Marina", Wire: "Notizie marine", marina: "Informazioni marina e ormeggio", wire: "Briefing marittimo Marstick Wire" },
  fr: { Regulation: "Réglementation", Marina: "Marina", Wire: "Actualités maritimes", marina: "Informations marina et amarrage", wire: "Briefing maritime Marstick Wire" },
  de: { Regulation: "Vorschrift", Marina: "Marina", Wire: "Maritime Nachrichten", marina: "Marina- und Liegeplatzinformationen", wire: "Marstick Wire Briefing" },
  pt: { Regulation: "Regulamento", Marina: "Marina", Wire: "Notícias marítimas", marina: "Informações de marina e berço", wire: "Briefing marítimo Marstick Wire" },
  ar: { Regulation: "لائحة", Marina: "مرسى", Wire: "أخبار بحرية", marina: "معلومات المرسى والرسو", wire: "موجز Marstick Wire البحري" },
  el: { Regulation: "Κανονισμός", Marina: "Μαρίνα", Wire: "Ναυτικά νέα", marina: "Πληροφορίες μαρίνας και θέσης", wire: "Ναυτική ενημέρωση Marstick Wire" },
  ru: { Regulation: "Правило", Marina: "Марина", Wire: "Морские новости", marina: "Информация о марине и стоянке", wire: "Морской брифинг Marstick Wire" },
  tr: { Regulation: "Kural", Marina: "Marina", Wire: "Deniz haberleri", marina: "Marina ve bağlama bilgisi", wire: "Marstick Wire denizcilik özeti" },
  zh: { Regulation: "法规", Marina: "码头", Wire: "海事新闻", marina: "码头和泊位信息", wire: "Marstick Wire 海事简报" },
};

export default function GlobalHeaderSearch({ lang = "en" }) {
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(false);
  const navigate = useNavigate();
  const normalizedQuery = query.trim().toLowerCase();
  const shouldSearch = normalizedQuery.length >= 2;
  const t = searchCopy[lang];
  const labels = resultLabels[lang];
  const isRtl = languageLabels[lang]?.dir === "rtl";

  const { data: marinas = [] } = useQuery({
    queryKey: ["global-search-marinas"],
    queryFn: () => base44.entities.Marina.list("name", 300),
    enabled: shouldSearch,
    staleTime: 1000 * 60 * 10,
  });

  const { data: wireArticles = [] } = useQuery({
    queryKey: ["global-search-wire", lang],
    queryFn: () => base44.entities.WireArticle.filter({ status: "published", language: lang }, "-published_date", 200),
    enabled: shouldSearch,
    staleTime: 1000 * 60 * 10,
  });

  const regulationLinks = React.useMemo(() => {
    const hub = getRegulationsHubTranslation(lang, publicSeoHubs.regulations);
    return [
      { label: hub.title, description: hub.description, href: `/${lang}/regulations` },
      ...(hub.featuredPages || []).map((item) => ({
        label: item.label,
        description: item.description,
        href: item.href?.startsWith("/") ? `/${lang}${item.href}` : `/${lang}/regulations`,
      })),
    ];
  }, [lang]);

  const results = React.useMemo(() => {
    if (normalizedQuery.length < 2) return [];
    const matches = (text) => String(text || "").toLowerCase().includes(normalizedQuery);
    return [
      ...regulationLinks.filter((item) => matches(`${item.label} ${item.description}`)).map((item) => ({
        type: labels.Regulation,
        title: item.label,
        description: item.description,
        href: item.href,
      })),
      ...marinas.filter((item) => matches(`${item.name} ${item.city} ${item.country} ${item.location} ${item.description}`)).map((item) => ({
        type: labels.Marina,
        title: item.name,
        description: item.location || item.city || item.country || labels.marina,
        href: marinaDetailPath(item, lang),
      })),
      ...wireArticles.filter((item) => matches(`${item.title} ${item.excerpt} ${item.meta_description} ${item.category}`)).map((item) => ({
        type: labels.Wire,
        title: item.title,
        description: item.excerpt || item.meta_description || labels.wire,
        href: `/${lang}/wire/${item.slug}`,
      })),
    ].slice(0, 8);
  }, [labels, lang, marinas, normalizedQuery, regulationLinks, wireArticles]);

  if (!t || !labels) return null;

  const submitSearch = (event) => {
    event.preventDefault();
    const term = query.trim();
    if (!term) return;
    base44.entities.GlobalSearchTerm.create({
      term,
      normalized_term: term.toLowerCase(),
      language: lang,
      result_count: results.length,
      source_path: window.location.pathname,
      searched_at: new Date().toISOString(),
    }).catch(() => {});
    setActive(false);
    navigate(`/${lang}/search?q=${encodeURIComponent(term)}`);
  };

  return (
    <form onSubmit={submitSearch} className="relative hidden min-w-[14rem] max-w-xl flex-1 md:block">
      <Search className="pointer-events-none absolute top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-white/70" style={{ insetInlineStart: "1rem" }} />
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setActive(true)}
        onBlur={() => window.setTimeout(() => setActive(false), 160)}
        placeholder={t.placeholder}
        aria-label={t.aria}
        dir={isRtl ? "rtl" : "ltr"}
        style={{ paddingInlineStart: "2.75rem", paddingInlineEnd: "1rem", textAlign: isRtl ? "right" : "left" }}
        className="h-11 rounded-full border-slate-200 bg-white text-slate-950 shadow-sm placeholder:text-slate-500 dark:border-white/15 dark:bg-white/10 dark:text-white dark:placeholder:text-white/60"
      />

      {active && (
        <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-[70] overflow-hidden rounded-3xl border border-slate-200 bg-white text-slate-950 shadow-2xl shadow-ocean/20 dark:border-white/10 dark:bg-slate-950 dark:text-white">
          {normalizedQuery.length < 2 ? (
            <div className="p-4 text-sm font-bold text-slate-600">{t.minChars}</div>
          ) : results.length ? (
            <div className="max-h-[28rem] overflow-y-auto p-2">
              {results.map((result) => {
                const Icon = iconMap[result.type] || MapPin;
                return (
                  <Link key={`${result.type}-${result.href}`} to={result.href} className="flex gap-3 rounded-2xl p-3 transition hover:bg-primary/10" onClick={() => setActive(false)}>
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-black uppercase tracking-widest text-primary">{result.type}</span>
                      <span className="block truncate text-sm font-black text-slate-950 dark:text-white">{result.title}</span>
                      <span className="block truncate text-xs text-slate-600 dark:text-white/70">{result.description}</span>
                    </span>
                    <ArrowRight className="mt-3 h-4 w-4 shrink-0 text-slate-400" />
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="p-4 text-sm font-bold text-slate-600">{t.noMatches}</div>
          )}
        </div>
      )}
    </form>
  );
}